import moment from 'moment';

class LocationDataCache {
  constructor(options) {
    // Map of deviceId -> [block]
    this.deviceBlocks = {};
    this.mapMarkerIcons = {};
    this.mapMarkerIconLocalVersions = {};
    this.latestMapMarkerIconRequestSequence = 0;
    this.acceptedMapMarkerIconRequestSequences = {};
    this.locationDataService = options.locationDataService;
  }

  /**
   * Loads the latest device state while preventing stale responses from rolling
   * back a newer map-marker icon selection.
   */
  getLatestLocations(deviceIds) {
    const requestSequence = this._nextMapMarkerIconRequestSequence();
    const localVersions = {};
    deviceIds.forEach((deviceId) => {
      localVersions[deviceId] = this._mapMarkerIconLocalVersion(deviceId);
    });

    return this.locationDataService.loadLatestLocations(deviceIds).then((devices) => {
      devices.forEach((device) => {
        this._resolveMapMarkerIconResponse(
          device.id,
          device,
          requestSequence,
          localVersions[device.id] || 0,
        );
      });
      return devices;
    });
  }

  /**
   * Records an icon selected locally and updates every cached history block for
   * the device so live and playback maps stay consistent.
   */
  updateMapMarkerIcon(deviceId, mapMarkerIcon) {
    this.mapMarkerIconLocalVersions[deviceId]
      = this._mapMarkerIconLocalVersion(deviceId) + 1;
    this._recordMapMarkerIcon(deviceId, mapMarkerIcon);
  }

  /**
   * Stores an accepted icon value and applies it to existing history blocks.
   */
  _recordMapMarkerIcon(deviceId, mapMarkerIcon) {
    if (typeof mapMarkerIcon === 'undefined') {
      return;
    }

    this.mapMarkerIcons[deviceId] = mapMarkerIcon;

    const blocks = this.deviceBlocks[deviceId] || [];
    blocks.forEach((block) => {
      block.device.mapMarkerIcon = mapMarkerIcon;
    });
  }

  /**
   * Query a devices location at or before the given time t.
   */
  getLocationAtOrBefore(deviceId, t) {
    this.info(`getDeviceAt(${deviceId}, ${t}) - start`);

    return this._findOrLoadBlockAt(deviceId, t)
      .then(block => ({
        id: block.device.id,
        imei: block.device.imei,
        name: block.device.name,
        model: block.device.model,
        mapMarkerIcon: block.device.mapMarkerIcon,
        audioEnabled: block.device.audioEnabled,
        phoneNumber: block.device.phoneNumber,
        position: block.locationAtOrBefore(t),
      }));
  }

  /*
   * Query a devices location data between t1 and t2.
   *
   * Returns a LocationBlock instance containing location[].
   */
  getLocationsBetween(deviceId, t1, t2) {
    const t1Moment = moment(t1);
    const t2Moment = moment(t2);
    this.info(`getLocationsBetween(${deviceId}, ${t1Moment}, ${t2Moment}) - start`);
    return this._findOrLoadBlockBetween(deviceId, t1Moment, t2Moment);
  }

  /**
   * Retrieves a location block matching the given criteria
   * (device, includes t). If a matching block already exists in the cache
   * then it will be returned. Failing that the block will be retrieved
   * from the LocationDataService, stored in the cache and then returned.
   */
  _findOrLoadBlockAt(deviceId, t) {
    const existingBlock = this.deviceBlocks[deviceId]
      && this.deviceBlocks[deviceId].find(block => block.includesTime(t));

    if (existingBlock) {
      this.info(`_findOrLoadBlockAt(${deviceId}, ${t}) - cache hit`);
      return new Promise(resolve => resolve(existingBlock));
    }

    this.info(`_findOrLoadBlockAt(${deviceId}, ${t}) - cache miss`);

    // Calculate reasonable block based off of time T
    const t1 = moment(t).subtract(1, 'hour');
    let t2 = moment(t).add(1, 'hour');
    if (t2.isAfter()) {
      t2 = moment();
    }
    return this._loadBlock(deviceId, t1, t2);
  }

  /**
   * Retrieves a location block matching the given criteria
   * (device, time range). If a matching block already exists in the cache
   * then it will be returned. Failing that the block will be retrieve
   * from the LocationDataService, stored in the cached and then returned.
   */
  _findOrLoadBlockBetween(deviceId, t1, t2) {
    const existingBlock = this.deviceBlocks[deviceId]
      && this.deviceBlocks[deviceId]
        .find(block => block.includesDuration(t1, t2));

    if (existingBlock) {
      this.info(`_findOrLoadBlockBetween(${deviceId}, ${t1}, ${t2}) - cache hit`);
      return new Promise(resolve => resolve(existingBlock));
    }

    this.info(`_findOrLoadBlockBetween(${deviceId}, ${t1}, ${t2}) - cache miss`);
    return this._loadBlock(deviceId, t1, t2);
  }

  /**
   * Loads a LocationBlock into the the cache by first retrieving it from
   * the LocationDataService and then saving it to the cache before finally
   * returning it.
   */
  _loadBlock(deviceId, t1, t2) {
    this.info(`_loadBlock(${deviceId}, ${t1}, ${t2}) - start`);
    const requestSequence = this._nextMapMarkerIconRequestSequence();
    const localVersion = this._mapMarkerIconLocalVersion(deviceId);

    return this.locationDataService
      .loadLocationsBetween([deviceId], t1, t2).then((blocks) => {
        // TODO: Optimize and support loading of multiple blocks
        const block = blocks[0];
        this._saveBlock(deviceId, block, requestSequence, localVersion);
        return block;
      });
  }

  /**
   * Saves the given LocationBlock into the cache for deviceId.
   */
  _saveBlock(deviceId, block, requestSequence, localVersion) {
    this.info(`_saveBlock(${deviceId}, ${block}) - saving (${block.positions.length} positions)`);

    this._resolveMapMarkerIconResponse(
      deviceId,
      block.device,
      requestSequence,
      localVersion,
    );

    // TODO: Better merging of block into cache since there may be overlap
    let deviceBlocks = this.deviceBlocks[deviceId];
    if (!deviceBlocks) {
      deviceBlocks = [];
      this.deviceBlocks[deviceId] = deviceBlocks;
    }

    deviceBlocks.push(block);
  }

  /**
   * Accepts only the newest server response unless a newer local selection has
   * already superseded it.
   */
  _resolveMapMarkerIconResponse(deviceId, device, requestSequence, localVersion) {
    const localIconChanged = this._mapMarkerIconLocalVersion(deviceId) > localVersion;
    const isNewerRequest = requestSequence
      > (this.acceptedMapMarkerIconRequestSequences[deviceId] || 0);

    if (localIconChanged || !isNewerRequest) {
      this._applyCurrentMapMarkerIcon(deviceId, device);
    } else {
      this.acceptedMapMarkerIconRequestSequences[deviceId] = requestSequence;
      this._recordMapMarkerIcon(deviceId, device.mapMarkerIcon);
    }
  }

  /**
   * Reconciles a response object to the latest accepted icon for its device.
   */
  _applyCurrentMapMarkerIcon(deviceId, device) {
    if (Object.prototype.hasOwnProperty.call(this.mapMarkerIcons, deviceId)) {
      device.mapMarkerIcon = this.mapMarkerIcons[deviceId];
      return true;
    }

    return false;
  }

  /**
   * Returns the local-selection version used to detect in-flight stale responses.
   */
  _mapMarkerIconLocalVersion(deviceId) {
    return this.mapMarkerIconLocalVersions[deviceId] || 0;
  }

  /**
   * Allocates a monotonically increasing sequence number to each icon-bearing request.
   */
  _nextMapMarkerIconRequestSequence() {
    this.latestMapMarkerIconRequestSequence += 1;
    return this.latestMapMarkerIconRequestSequence;
  }

  info(msg) {
    //console.log(`LocationDataCache: ${msg}`);
  }
}

export default LocationDataCache;
